import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAniList } from '../context/AniListContext';
import { useTitlePreference } from '../context/TitlePreferenceContext';
import { useAlert } from '../context/ToastContext';
import { useAdultContent } from '../context/AdultContentContext';
import { ANILIST_PROXY_URL } from '../config/constants';
import { addAnimeWithRetry, updateAnimeStatus } from '../services/aniListService';
import { getMangaMediaStatusBadge } from '../utils/timeFormatter';
import { useMangaSource } from '../context/MangaSourceContext';
import CustomSelect from './common/CustomSelect';
import StatusButton from './StatusButton';
import LoadingSpinner from './common/LoadingSpinner';
import './Search.css';
import './Dashboard.css';

// AddToListButton component for manga not in user's list
const AddToListButton = ({ onAdd, isAdding, onOpenChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = React.useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        if (onOpenChange) onOpenChange(isOpen);
    }, [isOpen, onOpenChange]);

    const addOptions = [
        { value: 'PLANNING', label: 'Add to Planning', emoji: '📅', color: '#FF9800' },
        { value: 'CURRENT', label: 'Add to Reading', emoji: '📖', color: '#4CAF50' },
        { value: 'COMPLETED', label: 'Add to Completed', emoji: '✅', color: '#2196F3' }
    ];

    return (
        <div className="add-to-list-wrapper" ref={dropdownRef}>
            <button
                className={`add-to-list-button ${isAdding ? 'loading' : ''}`}
                onClick={(e) => { e.stopPropagation(); if (!isAdding) setIsOpen(!isOpen); }}
                disabled={isAdding}
                title="Add to list"
            >
                <span className="add-to-list-emoji">➕</span>
                <span className="add-to-list-label">{isAdding ? 'Adding...' : 'Add to List'}</span>
                <span className="add-to-list-arrow">{isOpen ? '▲' : '▼'}</span>
            </button>
            {isOpen && (
                <div className="add-to-list-dropdown">
                    {addOptions.map(option => (
                        <button key={option.value} className="add-to-list-option"
                            onClick={(e) => { e.stopPropagation(); onAdd(option.value); setIsOpen(false); }}
                            style={{ borderLeftColor: option.color }}>
                            <span className="add-option-emoji">{option.emoji}</span>
                            <span className="add-option-label">{option.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const MangaSearch = () => {
    const { token, refreshData } = useAniList();
    const { mangaUserData } = useMangaSource();
    const { titlePreference } = useTitlePreference();
    const { showError } = useAlert();
    const { hideAdultContent } = useAdultContent();

    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState({ format: '', status: '', year: '' });
    const [sortBy, setSortBy] = useState('none');
    const [sortDirection, setSortDirection] = useState('desc');
    const [selectedManga, setSelectedManga] = useState(null);
    const [userMangaMap, setUserMangaMap] = useState(new Map());
    const [optimisticUpdates, setOptimisticUpdates] = useState(new Map());
    const [changingStatus, setChangingStatus] = useState(new Set());
    const [addingToList, setAddingToList] = useState(new Set());
    const [dropdownOpenId, setDropdownOpenId] = useState(null);
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const searchTimerRef = useRef(null);
    const abortControllerRef = useRef(null);
    const searchVersionRef = useRef(0);

    const sortOptions = [
        { value: 'name', label: 'Name', icon: '📝' },
        { value: 'score', label: 'Score', icon: '⭐' }
    ];

    const getPreferredTitle = (manga) => {
        if (!manga || !manga.title) return 'Unknown';
        if (titlePreference === 'romanji') return manga.title.romaji || manga.title.english || 'Unknown';
        return manga.title.english || manga.title.romaji || manga.title.native || 'Unknown';
    };

    // Build user manga map
    useEffect(() => {
        if (!mangaUserData || !mangaUserData.lists) return;
        const map = new Map();
        mangaUserData.lists.forEach(list => {
            list.entries.forEach(entry => map.set(entry.media.id, entry.status));
        });
        setUserMangaMap(map);
    }, [mangaUserData]);

    // Search AniList for manga
    const performSearch = useCallback(async (query, currentFilters, signal, version) => {
        const trimmed = query.trim();
        if (!trimmed && !currentFilters.format && !currentFilters.status && !currentFilters.year) {
            setResults([]);
            setHasSearched(false);
            return;
        }

        setIsSearching(true);
        setHasSearched(true);

        try {
            // Build dynamic query variables
            const variables = {};
            let filterArgs = 'type: MANGA, sort: SEARCH_MATCH';
            if (trimmed) { variables.search = trimmed; filterArgs += ', search: $search'; }
            if (currentFilters.format) { variables.format = currentFilters.format; filterArgs += ', format: $format'; }
            if (currentFilters.status) { variables.status = currentFilters.status; filterArgs += ', status: $status'; }
            if (currentFilters.year) { variables.seasonYear = parseInt(currentFilters.year); filterArgs += ', seasonYear: $seasonYear'; }

            const varDefs = [];
            if (variables.search) varDefs.push('$search: String');
            if (variables.format) varDefs.push('$format: MediaFormat');
            if (variables.status) varDefs.push('$status: MediaStatus');
            if (variables.seasonYear) varDefs.push('$seasonYear: Int');

            const graphqlQuery = {
                query: `
                    query (${varDefs.join(', ')}) {
                        Page(page: 1, perPage: 50) {
                            media(${filterArgs}) {
                                id
                                title { romaji english native }
                                coverImage { large medium extraLarge }
                                bannerImage
                                format
                                status
                                chapters
                                volumes
                                averageScore
                                genres
                                isAdult
                                startDate { year month day }
                                description(asHtml: true)
                            }
                        }
                    }
                `,
                variables
            };

            const response = await fetch(ANILIST_PROXY_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    query: graphqlQuery.query,
                    variables: graphqlQuery.variables,
                    ...(token ? { token } : {})
                }),
                signal
            });

            const data = await response.json();
            if (version === searchVersionRef.current) {
                const rawResults = data?.data?.Page?.media || [];
                const filtered = hideAdultContent
                    ? rawResults.filter(item => !item.isAdult)
                    : rawResults;
                setResults(filtered);
                setIsSearching(false);
            }
        } catch (error) {
            if (error.name === 'AbortError') return;
            console.error('Search failed:', error);
            if (version === searchVersionRef.current) {
                setResults([]);
                setIsSearching(false);
            }
        }
    }, [token, hideAdultContent]);

    // Debounced search
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        if (abortControllerRef.current) abortControllerRef.current.abort();
        searchVersionRef.current += 1;
        const currentVersion = searchVersionRef.current;
        searchTimerRef.current = setTimeout(() => {
            const controller = new AbortController();
            abortControllerRef.current = controller;
            performSearch(searchQuery, filters, controller.signal, currentVersion);
        }, 450);
        return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
    }, [searchQuery, filters, performSearch]);

    // Handle status change for manga already in user's list
    const handleStatusChange = async (mediaId, currentStatus, newStatus, mangaTitle) => {
        if (newStatus === currentStatus) return;

        if (!token) {
            showError('Authentication required. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        // Optimistic update - update UI immediately
        setOptimisticUpdates(prev => new Map(prev).set(mediaId, newStatus));
        setChangingStatus(prev => new Set([...prev, mediaId]));

        try {
            await updateAnimeStatus(mediaId, newStatus, token);
            console.log(`✅ Updated ${mangaTitle} to ${newStatus}`);

            // Update the userMangaMap
            setUserMangaMap(prev => new Map(prev).set(mediaId, newStatus));

            // Trigger refresh to get fresh data
            if (refreshData) {
                refreshData(true);
            }

            setChangingStatus(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
        } catch (error) {
            console.error(`❌ Failed to update status for ${mangaTitle}:`, error);

            // Revert optimistic update on error
            setOptimisticUpdates(prev => {
                const next = new Map(prev);
                next.delete(mediaId);
                return next;
            });

            setChangingStatus(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });

            showError(`Failed to update status: ${error.message}`, 'Status Update Failed');
        }
    };

    // Handle adding manga to list (for manga not in user's list)
    const handleAddToList = async (mediaId, status, title) => {
        if (!token) {
            showError('Authentication required. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        // Optimistic update
        setOptimisticUpdates(prev => new Map(prev).set(mediaId, status));
        setAddingToList(prev => new Set([...prev, mediaId]));

        try {
            await addAnimeWithRetry(mediaId, status, token);
            console.log(`✅ Added ${title} to list as ${status}`);

            // Update the userMangaMap
            setUserMangaMap(prev => new Map(prev).set(mediaId, status));

            if (refreshData) refreshData(true);

            setAddingToList(prev => { const next = new Set(prev); next.delete(mediaId); return next; });
        } catch (error) {
            console.error(`❌ Failed to add ${title} to list:`, error);

            // Revert optimistic update on error
            setOptimisticUpdates(prev => {
                const next = new Map(prev);
                next.delete(mediaId);
                return next;
            });

            setAddingToList(prev => { const next = new Set(prev); next.delete(mediaId); return next; });
            showError(`Failed to add to list: ${error.message}`, 'Add Failed');
        }
    };

    const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
    const clearFilters = () => { setFilters({ format: '', status: '', year: '' }); setSearchQuery(''); setSortBy('none'); setResults([]); };

    const formatMangaInfo = (manga) => {
        const parts = [];
        const formatLabels = { 'MANGA': 'Manga', 'LIGHT_NOVEL': 'Light Novel', 'ONE_SHOT': 'One Shot', 'NOVEL': 'Novel', 'MANHWA': 'Manhwa', 'MANHUA': 'Manhua' };
        if (manga.format) parts.push(formatLabels[manga.format] || manga.format);
        if (manga.chapters) parts.push(`${manga.chapters} ch`);
        if (manga.volumes) parts.push(`${manga.volumes} vol`);
        const statusLabels = { 'FINISHED': 'Finished', 'RELEASING': 'Publishing', 'NOT_YET_RELEASED': 'Upcoming', 'CANCELLED': 'Cancelled', 'HIATUS': 'Hiatus' };
        if (manga.status) parts.push(statusLabels[manga.status] || manga.status);
        return parts.join(' • ');
    };

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (selectedManga) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = 'unset';
        return () => { document.body.style.overflow = 'unset'; };
    }, [selectedManga]);

    // Sort results
    const sortedResults = React.useMemo(() => {
        if (!results.length || sortBy === 'none') return results;
        return [...results].sort((a, b) => {
            if (sortBy === 'name') {
                const na = getPreferredTitle(a).toLowerCase();
                const nb = getPreferredTitle(b).toLowerCase();
                return sortDirection === 'asc' ? na.localeCompare(nb) : nb.localeCompare(na);
            }
            if (sortBy === 'score') {
                const sa = a.averageScore || 0;
                const sb = b.averageScore || 0;
                return sortDirection === 'asc' ? sa - sb : sb - sa;
            }
            return 0;
        });
    }, [results, sortBy, sortDirection, titlePreference]);

    const formatOptions = [
        { value: 'MANGA', label: 'Manga', icon: '📖', color: 'format-manga' },
        { value: 'LIGHT_NOVEL', label: 'Light Novel', icon: '📕', color: 'format-ln' },
        { value: 'ONE_SHOT', label: 'One Shot', icon: '📄', color: 'format-oneshot' },
        { value: 'NOVEL', label: 'Novel', icon: '📚', color: 'format-novel' },
        { value: 'MANHWA', label: 'Manhwa', icon: '🇰🇷', color: 'format-manhwa' },
        { value: 'MANHUA', label: 'Manhua', icon: '🇨🇳', color: 'format-manhua' },
    ];

    const statusOptions = [
        { value: 'FINISHED', label: 'Finished', icon: '✅', color: 'status-finished' },
        { value: 'RELEASING', label: 'Publishing', icon: '📖', color: 'status-releasing' },
        { value: 'NOT_YET_RELEASED', label: 'Not Yet Released', icon: '📅', color: 'status-upcoming' },
        { value: 'CANCELLED', label: 'Cancelled', icon: '❌', color: 'status-cancelled' },
        { value: 'HIATUS', label: 'Hiatus', icon: '⏸️', color: 'status-hiatus' },
    ];

    return (
        <div className="discover-container">
            <div className="discover-header">
                <h1>🔍 Discover Manga</h1>
            </div>

            {/* Search Box */}
            <div className="search-box">
                <div className="search-input-wrapper">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search by title..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                    />
                    {searchQuery && (
                        <button className="clear-search-btn" onClick={() => setSearchQuery('')} title="Clear search">✕</button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="search-filters">
                <CustomSelect value={sortBy} onChange={(value) => setSortBy(value)} options={sortOptions} placeholder="Sort" />
                {sortBy && sortBy !== 'none' && (
                    <button onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')} className="sort-direction-btn" title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                    </button>
                )}
                <CustomSelect value={filters.format} onChange={(value) => handleFilterChange('format', value)} options={formatOptions} placeholder="Format" />
                <CustomSelect value={filters.status} onChange={(value) => handleFilterChange('status', value)} options={statusOptions} placeholder="Status" />
                <CustomSelect
                    value={filters.year}
                    onChange={(value) => handleFilterChange('year', value)}
                    options={Array.from({ length: new Date().getFullYear() + 2 - 1940 }, (_, i) => {
                        const y = new Date().getFullYear() + 1 - i;
                        return { value: y.toString(), label: y.toString() };
                    })}
                    placeholder="Year"
                    searchable={true}
                />
                {(filters.format || filters.status || filters.year || searchQuery || (sortBy && sortBy !== 'none')) && (
                    <button onClick={clearFilters} className="clear-filters-btn" title="Clear all filters">Clear All</button>
                )}
            </div>

            {/* Results */}
            <div className="search-results">
                {isSearching && results.length === 0 && (
                    <div className="no-results"><p>🔍 Searching...</p></div>
                )}

                {results.length === 0 && !isSearching && hasSearched && searchQuery.trim().length >= 1 && (
                    <div className="no-results">
                        <p>No manga found matching "{searchQuery}"</p>
                        <p className="no-results-hint">Check the spelling or try different keywords</p>
                    </div>
                )}

                {sortedResults.length > 0 && (
                    <>
                        <div className="results-count">
                            Found {sortedResults.length} manga{sortedResults.length === 50 ? ' (showing top 50)' : ''}
                        </div>
                        <div className="overview-groups-grid">
                            {sortedResults.map(manga => {
                                const isUnreleased = manga.status === 'NOT_YET_RELEASED';
                                return (
                                    <div
                                        key={manga.id}
                                        className={`overview-group-card ${dropdownOpenId === manga.id ? 'dropdown-open' : ''}`}
                                        onClick={() => setSelectedManga(manga)}
                                    >
                                        {manga.coverImage?.large && (
                                            <img src={manga.coverImage.large} alt={getPreferredTitle(manga)} className="overview-cover" loading="lazy" />
                                        )}
                                        <div className="overview-group-info">
                                            <h3 className="overview-group-name">
                                                <a href={`https://anilist.co/manga/${manga.id}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                                    {getPreferredTitle(manga)}
                                                </a>
                                            </h3>
                                            {(() => { const badge = getMangaMediaStatusBadge(manga.status); return <span className={`media-status-badge ${badge.className}`}>{badge.emoji} {badge.label}</span>; })()}
                                            <div className="overview-stats">
                                                {manga.startDate?.year && <span className="stat-year">📅 {manga.startDate.year}</span>}
                                                {manga.chapters && !isUnreleased && (
                                                    <>{manga.startDate?.year && <span>•</span>}<span className="stat-episodes">📖 {manga.chapters} ch</span></>
                                                )}
                                                {manga.volumes && !isUnreleased && (
                                                    <><span>•</span><span className="stat-episodes">📕 {manga.volumes} vol</span></>
                                                )}
                                                {manga.averageScore && (
                                                    <><span>•</span><span className="stat-score">⭐ {(manga.averageScore / 10).toFixed(1)}</span></>
                                                )}
                                            </div>
                                            {manga.genres && manga.genres.length > 0 && (
                                                <div className="anime-genres">
                                                    {manga.genres.slice(0, 3).map((genre, idx) => (
                                                        <span key={idx} className="genre-tag">{genre}</span>
                                                    ))}
                                                </div>
                                            )}
                                            {/* Status Button Section */}
                                            <div className="discover-status-section" onClick={(e) => e.stopPropagation()}>
                                                {(() => {
                                                    const mangaTitle = getPreferredTitle(manga);
                                                    const currentStatus = userMangaMap.get(manga.id);
                                                    const optimisticStatus = optimisticUpdates.get(manga.id);
                                                    const displayStatus = optimisticStatus || currentStatus;
                                                    const isChanging = changingStatus.has(manga.id);
                                                    const isAdding = addingToList.has(manga.id);

                                                    // Manga is in user's list - show status button
                                                    if (currentStatus || optimisticStatus) {
                                                        return (
                                                            <StatusButton
                                                                currentStatus={displayStatus}
                                                                onChange={(newStatus) => handleStatusChange(manga.id, displayStatus, newStatus, mangaTitle)}
                                                                disabled={isChanging}
                                                                isLoading={isChanging}
                                                            />
                                                        );
                                                    }

                                                    // Manga is NOT in user's list - show "Add to List" button
                                                    return (
                                                        <AddToListButton
                                                            onAdd={(status) => handleAddToList(manga.id, status, mangaTitle)}
                                                            isAdding={isAdding}
                                                            onOpenChange={(isOpen) => setDropdownOpenId(isOpen ? manga.id : null)}
                                                        />
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {/* Manga Details Modal */}
            {selectedManga && (
                <div className="anime-modal-overlay" onClick={() => setSelectedManga(null)}>
                    <div className="anime-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close-btn" onClick={() => setSelectedManga(null)}>✕</button>
                        {selectedManga.bannerImage && (
                            <img src={selectedManga.bannerImage} alt="" className="anime-banner" />
                        )}
                        <div className="modal-content">
                            <div className="modal-header">
                                {selectedManga.coverImage?.large && (
                                    <img src={selectedManga.coverImage.large} alt={getPreferredTitle(selectedManga)} className="modal-cover" />
                                )}
                                <div className="modal-header-info">
                                    <h2>
                                        <a href={`https://anilist.co/manga/${selectedManga.id}`} target="_blank" rel="noopener noreferrer">
                                            {getPreferredTitle(selectedManga)}
                                        </a>
                                    </h2>
                                    {selectedManga.title.english && selectedManga.title.english !== selectedManga.title.romaji && (
                                        <p className="alt-title">{selectedManga.title.romaji}</p>
                                    )}
                                    <p className="anime-meta">{formatMangaInfo(selectedManga)}</p>
                                    {selectedManga.averageScore && (
                                        <div className="score-large">⭐ {selectedManga.averageScore / 10}/10</div>
                                    )}
                                </div>
                            </div>
                            {selectedManga.description && (
                                <div className="modal-description">
                                    <h3>Description</h3>
                                    <div dangerouslySetInnerHTML={{ __html: selectedManga.description }} />
                                </div>
                            )}
                            {selectedManga.genres && selectedManga.genres.length > 0 && (
                                <div className="modal-genres">
                                    <h3>Genres</h3>
                                    <div className="genre-list">
                                        {selectedManga.genres.map((genre, idx) => (
                                            <span key={idx} className="genre-tag-large">{genre}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="modal-actions">
                                <a href={`https://anilist.co/manga/${selectedManga.id}`} target="_blank" rel="noopener noreferrer" className="btn-anilist">
                                    View on AniList
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MangaSearch;
